import { useMemo } from "react"
import { ZodObject } from "zod"
import { ConfigDescription, ConfigurablePropsFromDescription } from "../helpers/widget"
import { GeneralWidgetConfig, generalWidgetConfigSchema } from "../components/WidgetConfiguration"

export function useWidgetConfig<
  T extends ReadonlyArray<ConfigDescription<any, any, any>>
>(
  configDef: T,
  containsConfigValues: ConfigurablePropsFromDescription<T>
): ConfigurablePropsFromDescription<T>

export function useWidgetConfig<
  T extends ReadonlyArray<ConfigDescription<any, any, any>>,
  R
>(
  configDef: T,
  containsConfigValues: ConfigurablePropsFromDescription<T>,
  transformer: (config: ConfigurablePropsFromDescription<T>) => R
): R

/**
 * Helper to get a stable reference to widget config options (returned reference only changes if configuration has changed),
 * with an optional transformer
 */
export function useWidgetConfig<
  T extends ReadonlyArray<ConfigDescription<any, any, any>>,
  R
>(
  configDef: T,
  containsConfigValues: ConfigurablePropsFromDescription<T>,
  transformer?: (config: ConfigurablePropsFromDescription<T>) => R
) {
  const config = justWidgetConfig(configDef, containsConfigValues)

  // this sucks but hard to do it the smart way (since useMemo dependency array length may not change)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(
    () => (transformer ? transformer(config) : config),
    [JSON.stringify(config)]
  )
}

/**
 * Create new object that *only* has the widget config and no other properties (which may change more often)
 */
export function justWidgetConfig<
  T extends ReadonlyArray<ConfigDescription<any, any, any>>,
  V = ConfigurablePropsFromDescription<T> & GeneralWidgetConfig
>(
  configDef: T,
  containsConfigValues: V
): V {
  const result: Partial<V> = {}

  const allowedKeys = new Set<string>([
    ...new Set(configDef.map(cd => cd.name)),
    ...new Set(Object.keys(generalWidgetConfigSchema.shape)),
  ])

  for (const key in containsConfigValues) {
    if (!allowedKeys.has(key)) continue
    result[key] = containsConfigValues[key]
  }

  return result as V
}